/**
 * L'écran du planning — la semaine, les camions, et ce qu'ils font.
 *
 * Copie de l'écran de la v1, validée sur maquette le 04/09/2026, avec les six
 * changements demandés le même jour : le chauffeur en information principale,
 * aucune barre de chiffres au-dessus des journées, plus d'étiquette de service,
 * l'ordre des lignes réglable, cinq jours par défaut et un seul filtre.
 *
 * LA DÉCOUPE EST CELLE DES AUTRES ÉCRANS : `VuePlanning` dessine et ne décide
 * rien, `EcranPlanning` tient l'état et parle à l'API. Tout ce qui se calcule
 * — la semaine, les blocs, les refus — est dans `planning.ts`, éprouvé sans
 * navigateur.
 *
 * LE GLISSER-DÉPOSER EST LE SEUL GESTE D'AFFECTATION, comme en v1 : on prend un
 * ordre dans la pile ou dans la grille, on le pose sur une case. Il ne marche
 * pas au doigt et ne s'atteint pas au clavier ; c'est dit une fois ici et ce
 * n'est pas un oubli, c'est ce qui a été demandé.
 *
 * UN CLIC SUR LE BLOC ouvre la fiche de l'ordre. C'est le seul endroit où cet
 * écran en quitte un autre : il rend l'identifiant à l'application, qui bascule
 * sur les ordres de transport et l'y ouvre.
 *
 * Défaut constaté dans le navigateur de l'utilisateur le 04/09/2026 : seul un
 * chevron de treize pixels, gris clair, ouvrait la fiche. Il fonctionnait —
 * mesuré, clic par clic — mais personne ne le voyait, et le geste naturel,
 * cliquer sur le bloc, ne faisait rien. Le bloc entier est donc devenu la cible.
 * Le chevron reste : c'est lui qui se voit, et c'est le seul chemin au clavier,
 * un `div` cliquable n'étant pas atteignable par tabulation.
 *
 * TROIS CHOSES QUE CET ÉCRAN NE FAIT PAS
 * ---------------------------------------------------------------------------
 *   - il ne crée ni ordre ni étape : le planning pose un camion sur ce qui
 *     existe déjà ;
 *   - il n'affiche aucun montant. La v1 met le résultat de la journée en haut
 *     de chaque case ; la v2 n'a pas de coût de revient, et la barre a été
 *     retirée à la demande de l'utilisateur.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  affecterAuPlanning,
  lirePlanning,
  rangerLesVehicules,
  type EchecApi,
} from './api.js';
import {
  abregesDe,
  blocsParCase,
  colonneDite,
  correspond,
  decaler,
  finDeSemaine,
  ordreApres,
  pile,
  refusDepot,
  reportDemande,
  semaineDe,
  semaineDite,
  teteDeLigne,
  type Bloc,
  type CartePile,
  type LigneBloc,
  type Planning,
  type Prise,
  type Semaine,
  type VehiculePlanning,
} from './planning.js';

/** Le jour d'aujourd'hui, dans le fuseau de celui qui regarde. */
function aujourdhui(): string {
  const maintenant = new Date();
  const mois = String(maintenant.getMonth() + 1).padStart(2, '0');
  const jour = String(maintenant.getDate()).padStart(2, '0');
  return `${maintenant.getFullYear()}-${mois}-${jour}`;
}

type Etat =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'pret'; readonly planning: Planning }
  | { readonly nom: 'panne' };

/** Ce que l'API a refusé, dit en français. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cet ordre n’existe plus. Rechargez l’écran.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Cette valeur est déjà prise.',
  invalide: 'Ce que l’écran a envoyé n’a pas été accepté. Rechargez et réessayez.',
  conflit: 'Deux périodes se recouvrent.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Impossible : quelque chose s’y rattache encore.',
  ordre_annule: 'Cet ordre est annulé : il ne se planifie pas.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape : rien à poser sur un camion.',
  etape_validee:
    'Une étape de cet ordre est déjà validée : ses dates ne se déplacent plus.',
  jour_sans_etape: 'Cet ordre n’a aucune étape ce jour-là. Rechargez l’écran.',
  // LE REFUS DES TOURNÉES, ajouté le 04/09/2026. Le numéro de la tournée est
  // LE NUMÉRO DE LA TOURNÉE ARRIVE DEPUIS LE 05/09/2026, et cette phrase-ci
  // n'est plus que le repli : elle sert quand le bloc n'en porte pas un seul —
  // deux tournées dans la même case, ou un dépôt venu de la pile. Le numéro ne
  // vient pas de l'API, dont `echec()` ne garde que le code : il vient du bloc,
  // que l'écran a déjà sous la main.
  etape_en_tournee:
    'Une étape de cet ordre appartient à une tournée. Sortez-l’en depuis l’écran des tournées avant de la déplacer.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
};

/** Le report en attente de réponse : on a lâché sur un autre jour. */
interface Report {
  readonly prise: Prise;
  readonly vehiculeId: string;
  readonly jour: string;
}

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

function LignesDuBloc({ lignes }: { readonly lignes: readonly LigneBloc[] }) {
  return (
    <>
      {lignes.map((ligne) => (
        <div
          key={ligne.id}
          className={ligne.autre ? 'ligne-etape autre' : 'ligne-etape'}
        >
          <span className={ligne.chargement ? 'sens chargement' : 'sens livraison'}>
            {ligne.chargement ? '▼' : '▲'}
          </span>
          <span className="lieu">
            {ligne.site}
            {ligne.departement === null ? null : (
              <span className="departement"> {ligne.departement}</span>
            )}
          </span>
          <span className="heure">
            {ligne.autre ? `${ligne.jour.slice(8)}/${ligne.jour.slice(5, 7)} ` : ''}
            {ligne.heure ?? ''}
          </span>
          <span className={ligne.litige ? 'marque litige' : 'marque'}>
            {ligne.litige ? '⚠' : ligne.fait ? '✓' : ''}
          </span>
          {ligne.marchandise === null ? null : (
            <span className="quantite">{ligne.marchandise}</span>
          )}
        </div>
      ))}
    </>
  );
}

export interface ProprietesVuePlanning {
  readonly etat: Etat;
  readonly semaine: Semaine;
  readonly septJours: boolean;
  readonly quandSeptJours: (sept: boolean) => void;
  readonly quandSemaine: (jour: string) => void;
  readonly recherche: string;
  readonly quandRecherche: (texte: string) => void;
  readonly refus: string | null;
  readonly cible: string | null;
  readonly prise: Prise | null;
  readonly quandPrise: (prise: Prise | null) => void;
  readonly quandCible: (cle: string | null) => void;
  readonly quandDepot: (vehiculeId: string, jour: string) => void;
  readonly quandLibere: () => void;
  readonly ligneSaisie: string | null;
  readonly quandLigneSaisie: (id: string | null) => void;
  readonly quandRanger: (avant: string | null) => void;
  /** Ouvrir la fiche d'un ordre, dans l'écran des ordres de transport. */
  readonly quandOuvrirOrdre: (otId: string) => void;
}

export function VuePlanning({
  etat,
  semaine,
  septJours,
  quandSeptJours,
  quandSemaine,
  recherche,
  quandRecherche,
  refus,
  cible,
  prise,
  quandPrise,
  quandCible,
  quandDepot,
  quandLibere,
  ligneSaisie,
  quandLigneSaisie,
  quandRanger,
  quandOuvrirOrdre,
}: ProprietesVuePlanning) {
  // OUVRIR ET GLISSER COMMENCENT PAR LE MÊME APPUI. Ce drapeau les distingue :
  // posé au départ d'un glisser, remis à zéro au début de l'appui suivant. Sans
  // lui, poser un ordre sur un camion ouvrirait sa fiche dans la foulée sur les
  // navigateurs qui envoient un clic après un dépôt.
  //
  // Un `useRef` et non un état : le changer ne doit rien redessiner.
  const aGlisse = useRef(false);

  if (etat.nom === 'chargement') {
    return <p className="attente">Chargement du planning…</p>;
  }
  if (etat.nom === 'panne') {
    return (
      <p className="attente">
        Stafa ne répond pas. Vérifiez votre connexion et rechargez la page.
      </p>
    );
  }

  // Les abrégés viennent de la société, avec le planning lui-même.
  const abreges = abregesDe(etat.planning.supports);
  const cases = blocsParCase(etat.planning.etapes, abreges);
  const groupes = pile(etat.planning.a_affecter, abreges);
  const ce = aujourdhui();

  const prendre = (
    otId: string,
    numero: string,
    jour: string | null,
    debut: string,
    statut: string,
    lignes: readonly LigneBloc[],
    tournee: string | null = null,
  ): Prise => ({
    ot_id: otId,
    numero,
    jour,
    debut,
    statut,
    valide: lignes.some((l) => l.fait),
    tournee,
  });

  /**
   * Ce qui rend un bloc OUVRABLE, en plus de déplaçable.
   *
   * Posé sur le bloc entier plutôt que sur le seul chevron : c'est là que la
   * main va. L'appui remet le drapeau à zéro, le glisser le pose, et le clic
   * qui suit un glisser est donc ignoré — on vient de déplacer l'ordre, pas de
   * demander à le lire.
   */
  const ouvrable = (otId: string) => ({
    title: 'Ouvrir cet ordre de transport',
    onMouseDown: () => {
      aGlisse.current = false;
    },
    onClick: () => {
      if (aGlisse.current) return;
      quandOuvrirOrdre(otId);
    },
  });

  return (
    <div className="planning">
      <div className="planning-barre">
        <button
          className="bouton bouton-discret"
          type="button"
          onClick={() => quandSemaine(decaler(semaine.du, -7) as string)}
          aria-label="Semaine précédente"
        >
          ‹
        </button>
        <button
          className="bouton bouton-discret"
          type="button"
          onClick={() => quandSemaine(decaler(semaine.du, 7) as string)}
          aria-label="Semaine suivante"
        >
          ›
        </button>
        <button
          className="bouton bouton-discret"
          type="button"
          onClick={() => quandSemaine(ce)}
        >
          Aujourd’hui
        </button>
        <span className="bascule">
          <button
            className={septJours ? 'bouton bouton-discret' : 'bouton bouton-choisi'}
            type="button"
            onClick={() => quandSeptJours(false)}
          >
            5 j
          </button>
          <button
            className={septJours ? 'bouton bouton-choisi' : 'bouton bouton-discret'}
            type="button"
            onClick={() => quandSeptJours(true)}
          >
            7 j
          </button>
        </span>
        <span className="titre-semaine">
          {semaineDite(semaine.du, semaine.au) ?? ''}
        </span>
        <input
          className="champ recherche"
          type="search"
          value={recherche}
          placeholder="Chercher : ordre, client, ville"
          onChange={(evenement) => quandRecherche(evenement.target.value)}
        />
      </div>

      <div className="planning-legende">
        <span>
          <i className="pastille attente" />à affecter
        </span>
        <span>
          <i className="pastille planifie" />
          planifié
        </span>
        <span>
          <i className="pastille encours" />
          en cours
        </span>
        <span>
          <i className="pastille livre" />
          livré
        </span>
        <span>
          <i className="pastille litige" />
          litige
        </span>
        <span className="sens chargement">▼ chargement</span>
        <span className="sens livraison">▲ livraison</span>
        <span className="marque-legende">✓ validée</span>
        <span className="aide">
          Glissez un ordre sur un camion pour l’affecter — ramenez-le à gauche pour le
          libérer.
        </span>
      </div>

      {refus === null ? null : <p className="bandeau-refus">{refus}</p>}

      <div className="planning-corps">
        <div
          className={cible === 'pile' ? 'pile-ot survol' : 'pile-ot'}
          onDragOver={(evenement) => {
            if (prise === null || prise.jour === null) return;
            evenement.preventDefault();
            quandCible('pile');
          }}
          onDragLeave={() => quandCible(null)}
          onDrop={(evenement) => {
            evenement.preventDefault();
            quandLibere();
          }}
        >
          <h2>
            À affecter
            <span>
              {(() => {
                const combien = groupes.reduce((t, g) => t + g.cartes.length, 0);
                return `${combien} ordre${combien > 1 ? 's' : ''}`;
              })()}
            </span>
          </h2>
          <div className="liste-pile">
            {groupes.length === 0 ? (
              <p className="vide">Tout est affecté.</p>
            ) : (
              groupes.map((groupe) => (
                <div key={groupe.titre}>
                  <p className="groupe-pile">{groupe.titre}</p>
                  {groupe.cartes
                    .filter((carte) => correspond(carte, recherche))
                    .map((carte: CartePile) => (
                      <div
                        key={carte.ot_id}
                        className={`bloc-ot ${carte.classe}${carte.litige ? ' litige' : ''}${
                          prise?.ot_id === carte.ot_id ? ' souleve' : ''
                        }`}
                        draggable
                        {...ouvrable(carte.ot_id)}
                        onDragStart={() => {
                          aGlisse.current = true;
                          quandPrise(
                            prendre(
                              carte.ot_id,
                              carte.numero,
                              null,
                              carte.du,
                              carte.statut,
                              carte.lignes,
                            ),
                          );
                        }}
                        onDragEnd={() => {
                          quandPrise(null);
                          quandCible(null);
                        }}
                      >
                        <div className="entete-bloc">
                          <span>{carte.numero}</span>
                          <button
                            className="ouvrir-ordre"
                            type="button"
                            title="Ouvrir cet ordre de transport"
                            onClick={(evenement) => {
                              // Le bloc écoute aussi : sans cela, ouvrir
                              // deux fois.
                              evenement.stopPropagation();
                              quandOuvrirOrdre(carte.ot_id);
                            }}
                          >
                            ›
                          </button>
                        </div>
                        <LignesDuBloc lignes={carte.lignes} />
                        <div className="client-bloc">{carte.client}</div>
                      </div>
                    ))}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="grille-planning">
          <table>
            <thead>
              <tr>
                <th className="colonne-camion">Chauffeur</th>
                {semaine.jours.map((jour) => (
                  <th key={jour} className={finDeSemaine(jour) ? 'we' : undefined}>
                    {colonneDite(jour)}
                    {jour === ce ? <span className="point"> •</span> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {etat.planning.vehicules.map((vehicule: VehiculePlanning) => {
                const tete = teteDeLigne(vehicule);
                return (
                  <tr key={vehicule.id}>
                    <td
                      className={
                        ligneSaisie === vehicule.id ? 'camion souleve' : 'camion'
                      }
                      onDragOver={(evenement) => {
                        if (ligneSaisie === null) return;
                        evenement.preventDefault();
                      }}
                      onDrop={(evenement) => {
                        if (ligneSaisie === null) return;
                        evenement.preventDefault();
                        quandRanger(vehicule.id);
                      }}
                    >
                      <span
                        className="poignee"
                        draggable
                        title="Glisser pour changer l’ordre des lignes"
                        onDragStart={() => quandLigneSaisie(vehicule.id)}
                        onDragEnd={() => quandLigneSaisie(null)}
                      >
                        ⠿
                      </span>
                      <div className={tete.sansChauffeur ? 'nom sans' : 'nom'}>
                        {tete.principal}
                      </div>
                      <div className="materiel">{tete.materiel}</div>
                    </td>

                    {semaine.jours.map((jour) => {
                      const cle = `${vehicule.id}|${jour}`;
                      const blocs = (cases.get(cle) ?? []).filter((bloc: Bloc) =>
                        correspond(bloc, recherche),
                      );
                      return (
                        <td
                          key={jour}
                          className={`case-jour${finDeSemaine(jour) ? ' we' : ''}${
                            cible === cle ? ' visee' : ''
                          }`}
                          onDragOver={(evenement) => {
                            if (prise === null) return;
                            evenement.preventDefault();
                            quandCible(cle);
                          }}
                          onDragLeave={() => {
                            if (cible === cle) quandCible(null);
                          }}
                          onDrop={(evenement) => {
                            if (prise === null) return;
                            evenement.preventDefault();
                            quandDepot(vehicule.id, jour);
                          }}
                        >
                          {blocs.map((bloc) => (
                            <div
                              key={`${bloc.ot_id}|${bloc.jour}`}
                              className={`bloc-ot ${bloc.classe}${
                                bloc.litige ? ' litige' : ''
                              }${bloc.suite_avant ? ' suite-avant' : ''}${
                                bloc.suite_apres ? ' suite-apres' : ''
                              }${prise?.ot_id === bloc.ot_id ? ' souleve' : ''}`}
                              draggable
                              {...ouvrable(bloc.ot_id)}
                              onDragStart={() => {
                                aGlisse.current = true;
                                quandPrise(
                                  prendre(
                                    bloc.ot_id,
                                    bloc.numero,
                                    bloc.jour,
                                    bloc.jour,
                                    bloc.statut,
                                    bloc.lignes,
                                    bloc.tournee,
                                  ),
                                );
                              }}
                              onDragEnd={() => {
                                quandPrise(null);
                                quandCible(null);
                              }}
                            >
                              <div className="entete-bloc">
                                <span>
                                  {bloc.suite_avant ? '‹ ' : ''}
                                  {bloc.numero}
                                </span>
                                {/* LE NUMÉRO DE TOURNÉE, décidé le 04/09/2026.
                                    Il dit aussi POURQUOI ce bloc ne se déplace
                                    pas : le planning refuse de bouger une étape
                                    en tournée, et sans ce numéro le refus
                                    n'indiquerait pas où aller la sortir. */}
                                {bloc.tournee === null ? null : (
                                  <span
                                    className="etiq-tournee"
                                    title={`Dans la tournée ${bloc.tournee} — sortez-la de sa tournée pour la déplacer`}
                                  >
                                    {bloc.tournee}
                                  </span>
                                )}
                                <button
                                  className="ouvrir-ordre"
                                  type="button"
                                  title="Ouvrir cet ordre de transport"
                                  onClick={(evenement) => {
                                    evenement.stopPropagation();
                                    quandOuvrirOrdre(bloc.ot_id);
                                  }}
                                >
                                  ›
                                </button>
                              </div>
                              <LignesDuBloc lignes={bloc.lignes} />
                              {bloc.repris_par === null ? null : (
                                <div className="relais">
                                  livré par <b>{bloc.repris_par}</b>
                                </div>
                              )}
                            </div>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'état
// ---------------------------------------------------------------------------

export interface ProprietesPlanning {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /** Demande à l'application d'ouvrir cet ordre dans l'écran des ordres. */
  readonly quandOuvrirOrdre: (otId: string) => void;
}

export function EcranPlanning({
  jeton,
  quandAccesPerdu,
  quandOuvrirOrdre,
}: ProprietesPlanning) {
  const [etat, setEtat] = useState<Etat>({ nom: 'chargement' });
  const [ancre, setAncre] = useState(aujourdhui());
  const [septJours, setSeptJours] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const [prise, setPrise] = useState<Prise | null>(null);
  const [cible, setCible] = useState<string | null>(null);
  const [ligneSaisie, setLigneSaisie] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);

  const semaine = semaineDe(ancre, septJours) as Semaine;

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /**
   * Toute la semaine en un seul appel.
   *
   * Le jour de RÉFÉRENCE est aujourd'hui quand la semaine le contient, et le
   * premier jour sinon : c'est lui qui décide quel chauffeur s'affiche en tête
   * de ligne. Sans cela, en regardant la semaine prochaine on lirait l'attelage
   * d'un lundi qui n'est pas à l'écran.
   */
  const charger = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;

    const ce = aujourdhui();
    const reference = ce >= semaine.du && ce <= semaine.au ? ce : semaine.du;
    const lu = await lirePlanning(j, {
      du: semaine.du,
      au: semaine.au,
      reference,
    });
    if (lu.etat !== 'ok') {
      if (lu.cause === 'acces') quandAccesPerdu();
      setEtat({ nom: 'panne' });
      return;
    }
    setEtat({ nom: 'pret', planning: lu.valeur });
  }, [avecJeton, quandAccesPerdu, semaine.du, semaine.au]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Une écriture, puis un rechargement : la base a le dernier mot. */
  const ecrire = (
    action: (j: string) => Promise<{ etat: 'ok' } | { etat: 'echec'; cause: EchecApi }>,
    /**
     * La tournée d'où part le bloc, quand on la connaît. **Elle ne vient pas de
     * l'API** : `echec()` ne garde que le code, et l'écran la tient déjà — le
     * bloc la porte depuis le 05/09/2026. C'est ce qui transforme « une étape
     * de cet ordre appartient à une tournée » en une phrase qui dit où aller.
     */
    tournee: string | null = null,
  ) => {
    setRefus(null);
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await action(j);
      if (resultat.etat === 'ok') {
        await charger();
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setRefus(
        resultat.cause === 'etape_en_tournee' && tournee !== null
          ? `Une étape de cet ordre appartient à la tournée ${tournee}. Sortez-l’en depuis l’écran de préparation avant de la déplacer.`
          : REFUS[resultat.cause],
      );
    })();
  };

  /**
   * Poser le camion, avec ou sans report.
   *
   * SANS report, `jour` est celui de la case d'où part le bloc — `null` pour
   * une carte de la pile, ce qui affecte l'ordre entier. AVEC report, l'API
   * exige le jour de départ : une carte de la pile donne alors le premier jour
   * de l'ordre, sans quoi le décalage n'aurait pas d'origine.
   */
  const poser = (prisePosee: Prise, vehiculeId: string, versLe: string | null) => {
    const depart = prisePosee.jour ?? prisePosee.debut;
    ecrire(
      (j) =>
        affecterAuPlanning(j, {
          ot_id: prisePosee.ot_id,
          jour: versLe === null ? prisePosee.jour : depart,
          vehicule_id: vehiculeId,
          vers_le: versLe,
        }),
      prisePosee.tournee,
    );
    setPrise(null);
    setCible(null);
  };

  return (
    <main className="corps corps-planning">
      <VuePlanning
        etat={etat}
        semaine={semaine}
        septJours={septJours}
        quandSeptJours={setSeptJours}
        quandSemaine={setAncre}
        recherche={recherche}
        quandRecherche={setRecherche}
        refus={refus}
        cible={cible}
        prise={prise}
        quandPrise={setPrise}
        quandCible={setCible}
        quandDepot={(vehiculeId, jour) => {
          if (prise === null) return;
          const refuse = refusDepot(prise, jour);
          if (refuse !== null) {
            setRefus(refuse);
            setPrise(null);
            setCible(null);
            return;
          }
          if (reportDemande(prise, jour)) {
            // Le jour change : on DEMANDE. Décaler les dates d'un transport
            // sans le dire serait le pire des deux — l'écran montrerait un
            // camion là où la base dit autre chose, ou l'inverse.
            setReport({ prise, vehiculeId, jour });
            setCible(null);
            return;
          }
          poser(prise, vehiculeId, null);
        }}
        quandLibere={() => {
          if (prise === null || prise.jour === null) return;
          ecrire((j) =>
            affecterAuPlanning(j, {
              ot_id: prise.ot_id,
              jour: prise.jour,
              vehicule_id: null,
            }),
          );
          setPrise(null);
          setCible(null);
        }}
        quandOuvrirOrdre={quandOuvrirOrdre}
        ligneSaisie={ligneSaisie}
        quandLigneSaisie={setLigneSaisie}
        quandRanger={(avant) => {
          if (ligneSaisie === null || etat.nom !== 'pret') return;
          const voulu = ordreApres(etat.planning.vehicules, ligneSaisie, avant);
          setLigneSaisie(null);
          ecrire((j) => rangerLesVehicules(j, voulu));
        }}
      />

      {report === null ? null : (
        <div className="voile-boite" role="presentation">
          <div className="boite-decrochage" role="dialog" aria-modal="true">
            <h3>
              Reporter l’ordre {report.prise.numero} au{' '}
              {colonneDite(report.jour) ?? report.jour} ?
            </h3>
            <p className="quoi-decroche">
              Il est aujourd’hui au{' '}
              {colonneDite(report.prise.jour ?? report.prise.debut) ?? ''}. Toutes ses
              étapes se décalent d’autant : sinon l’écran montrerait un transport d’un
              jour dans la colonne d’un autre.
            </p>
            <div className="pied-boite">
              <button
                className="bouton bouton-discret"
                type="button"
                onClick={() => {
                  setReport(null);
                  setPrise(null);
                }}
              >
                Annuler
              </button>
              <button
                className="bouton bouton-discret"
                type="button"
                onClick={() => {
                  poser(report.prise, report.vehiculeId, null);
                  setReport(null);
                }}
              >
                Affecter sans changer les dates
              </button>
              <button
                className="bouton"
                type="button"
                onClick={() => {
                  poser(report.prise, report.vehiculeId, report.jour);
                  setReport(null);
                }}
              >
                Décaler et affecter
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
